import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { useLiveQuery } from 'dexie-react-hooks';
import { apiService, posthog, userDB, userCache } from '@taleemabad/db';
import { useActiveRolePermission } from '@taleemabad/providers';
import { TextField, Button, Typography, SelectOption, Select as DSMSelect } from '@taleemabad/dsm/components';

const LOGIN_ISSUE_OPTIONS: SelectOption[] = [
 { value: 'sign_in', content: 'Sign-in issue' },
 { value: 'sign_up', content: 'Sign-up issue' },
 { value: 'otp', content: 'OTP issue' },
];

const LOGGED_IN_ISSUE_OPTIONS: SelectOption[] = [
 { value: 'timetable', content: 'Timetable' },
 { value: 'profile_management', content: 'Profile Management' },
 { value: 'teacher_training', content: 'Teacher Training' },
 { value: 'community', content: 'Community' },
 { value: 'profile_settings', content: 'Profile Settings' },
 { value: 'lesson_plan', content: 'Lesson Plan' },
 { value: 'feedback', content: 'Feedback & Suggestions' },
];

const ALL_ISSUE_OPTIONS = [...LOGIN_ISSUE_OPTIONS, ...LOGGED_IN_ISSUE_OPTIONS];

// Mapping of issue tags to Odoo Helpdesk team IDs
const ISSUE_TEAM_MAPPING: Record<string, number> = {
 // Login/Authentication issues → Team 2
 sign_in: 2,
 sign_up: 2,
 otp: 2,
 // Logged-in user issues → Different teams
 timetable: 3,
 profile_management: 4,
 teacher_training: 5,
 community: 6,
 profile_settings: 7,
 feedback: 8,
 lesson_plan: 9,
};

// Helper function to get team ID based on issue tag
const getTeamIdByIssueTag = (issueTag: string): number => {
 return ISSUE_TEAM_MAPPING[issueTag] || 2; // Default to team 2 if not found
};

// Odoo ticket field constants - centralized field definitions
const ODOO_TICKET_FIELDS = {
 // Core fields used for both single ticket details and ticket lists
 CORE: ['id', 'number', 'display_name', 'stage_id', 'create_date'] as const,

 // Additional fields needed only for ticket list view
 LIST_EXTENDED: ['name', 'description', 'partner_name', 'phone_number', 'user_name'] as const,
} as const;

// Helper to get fields for single ticket detail
const getTicketDetailFields = () => [...ODOO_TICKET_FIELDS.CORE];

// Helper to get fields for ticket list view
const getTicketListFields = () => [
 ...ODOO_TICKET_FIELDS.CORE,
 ...ODOO_TICKET_FIELDS.LIST_EXTENDED,
];

const NBSP_STRING = '&nbsp;';
const ISSUE_PREFIX = 'issue:';

const stripHtml = (input: string) => {
 if (!input) return '';
 if (typeof document !== 'undefined') {
   const temp = document.createElement('div');
   temp.innerHTML = input;
   return temp.textContent || temp.innerText || '';
 }
 return input
   .split('<')
   .map((segment) => {
     const closingIndex = segment.indexOf('>');
     return closingIndex >= 0 ? segment.slice(closingIndex + 1) : segment;
   })
   .join(' ')
   .split('>')
   .join(' ');
};

const extractIssueSummary = (desc: string) => {
 if (!desc) return '';
 const plainText = stripHtml(desc)
   .split(NBSP_STRING)
   .join(' ')
   .trim();

 const lowerText = plainText.toLowerCase();
 const issueIndex = lowerText.indexOf(ISSUE_PREFIX);
 const issueText =
   issueIndex >= 0 ? plainText.slice(issueIndex + ISSUE_PREFIX.length).trim() : plainText;

 return issueText.length > 150 ? `${issueText.slice(0, 150)}...` : issueText;
};

let fallbackRequestCounter = 0;
const generateRequestId = () => {
 const globalCrypto =
   typeof globalThis !== 'undefined'
     ? (globalThis.crypto || (globalThis as typeof globalThis & { msCrypto?: Crypto }).msCrypto)
     : undefined;
 if (globalCrypto?.randomUUID) {
   return globalCrypto.randomUUID();
 }
 if (globalCrypto?.getRandomValues) {
   const array = new Uint32Array(1);
   globalCrypto.getRandomValues(array);
   return array[0].toString(36);
 }
 fallbackRequestCounter += 1;
 return `${Date.now()}-${fallbackRequestCounter}`;
};

const getDeviceNameAndVersion = (): { name: string; version: string } => {
 if (typeof window === 'undefined' || typeof navigator === 'undefined') {
   return { name: 'Unknown Device', version: 'Unknown' };
 }

 const userAgent = navigator.userAgent;
 let deviceName = 'Unknown';
 let version = '';

 if (/iPhone|iPad|iPod/.test(userAgent)) {
   const match = userAgent.match(/OS (\d+)[._](\d+)/);
   if (match) {
     version = `iOS ${match[1]}.${match[2]}`;
   }
   if (/iPhone/.test(userAgent)) {
     deviceName = 'iPhone';
   } else if (/iPad/.test(userAgent)) {
     deviceName = 'iPad';
   } else if (/iPod/.test(userAgent)) {
     deviceName = 'iPod';
   }
 } else if (/Android/.test(userAgent)) {
   const match = userAgent.match(/Android (\d+(?:\.\d+)?)/);
   if (match) {
     version = `Android ${match[1]}`;
   }
   const deviceMatch = userAgent.match(/; ([^;)]+)\)/);
   if (deviceMatch) {
     deviceName = deviceMatch[1].trim();
   } else {
     deviceName = 'Android Device';
   }
 } else if (/Windows/.test(userAgent)) {
   deviceName = 'Windows';
   const match = userAgent.match(/Windows NT (\d+\.\d+)/);
   if (match) {
     const ntVersion = match[1];
     const versionMap: Record<string, string> = {
       '10.0': '10/11',
       '6.3': '8.1',
       '6.2': '8',
       '6.1': '7',
     };
     version = versionMap[ntVersion] || `NT ${ntVersion}`;
   }
 } else if (/Macintosh|Mac OS X/.test(userAgent)) {
   deviceName = 'Mac';
   const match = userAgent.match(/Mac OS X (\d+)[._](\d+)/);
   if (match) {
     version = `macOS ${match[1]}.${match[2]}`;
   }
 } else if (/Linux/.test(userAgent)) {
   deviceName = 'Linux';
   const match = userAgent.match(/Linux ([^;)]+)/);
   if (match) {
     version = match[1].trim();
   }
 }

 return {
   name: deviceName,
   version: version || 'Unknown',
 };
};

interface SupportWidgetProps {}

interface AttachmentPayload {
 filename: string;
 content_base64: string;
 mimetype: string;
 size: number;
}

// Helper function to validate attachment data
const isValidAttachment = (attachment: AttachmentPayload | null): boolean => {
 return !!(
   attachment &&
   attachment.content_base64 &&
   attachment.content_base64.length > 0
 );
};

// Attachment upload timing constants (in milliseconds)
const ATTACHMENT_DELAYS = {
 INITIAL_DELAY_IMAGE: 1000,        // 1 second for images/documents
 INITIAL_DELAY_VIDEO: 2000,        // 2 seconds for videos (larger files)
 RETRY_BASE_DELAY: 2000,           // Base delay for exponential backoff
 RETRY_MAX_DELAY: 15000,           // Max delay between retries (15 seconds)
} as const;

// Helper function to calculate initial delay based on file type
const getInitialDelay = (mimetype: string): number => {
 return mimetype.startsWith('video/')
   ? ATTACHMENT_DELAYS.INITIAL_DELAY_VIDEO
   : ATTACHMENT_DELAYS.INITIAL_DELAY_IMAGE;
};

// Helper function to calculate retry delay with exponential backoff
const getRetryDelay = (attempt: number): number => {
 return Math.min(
   ATTACHMENT_DELAYS.RETRY_BASE_DELAY * Math.pow(2, attempt - 1),
   ATTACHMENT_DELAYS.RETRY_MAX_DELAY
 );
};

// Helper function to attempt single attachment upload
const attemptAttachmentUpload = async (
 uid: number,
 ticketId: number,
 attachment: AttachmentPayload,
 callOdooProxy: (payload: any) => Promise<any>,
 generateRequestId: () => string,
): Promise<{ success: boolean; error?: any }> => {
 try {
   const attachData = await callOdooProxy({
     jsonrpc: '2.0',
     method: 'call',
     params: {
       service: 'object',
       method: 'execute_kw',
       args: [
         '', // db placeholder - Django backend replaces
         uid,
         '', // password placeholder - Django backend replaces
         'ir.attachment',
         'create',
         [
           {
             name: attachment.filename,
             datas: attachment.content_base64,
             res_model: 'helpdesk.ticket',
             res_id: ticketId,
             mimetype: attachment.mimetype || 'application/octet-stream',
           },
         ],
       ],
     },
     id: generateRequestId(),
   });

   if (attachData && !attachData.error && attachData.result) {
     return { success: true };
   }
   return { success: false, error: attachData?.error || { message: 'Upload failed' } };
 } catch (err: any) {
   const error = err.message?.includes('timeout')
     ? { type: 'timeout', message: 'Request timeout' }
     : err;
   return { success: false, error };
 }
};

// Helper function to upload attachment with retry logic (5 attempts with exponential backoff)
const uploadAttachmentWithRetry = async (
 uid: number,
 ticketId: number,
 attachment: AttachmentPayload,
 callOdooProxy: (payload: any) => Promise<any>,
 generateRequestId: () => string,
): Promise<void> => {
 if (!isValidAttachment(attachment)) {
   return;
 }

 const mimetype = attachment.mimetype || 'application/octet-stream';
 const initialDelay = getInitialDelay(mimetype);
 await new Promise((resolve) => setTimeout(resolve, initialDelay));

 const maxAttempts = 5; // Reduced from 20 to 5 for performance optimization
 let attachSuccess = false;

 for (let attempt = 1; attempt <= maxAttempts; attempt++) {
   const result = await attemptAttachmentUpload(
     uid,
     ticketId,
     attachment,
     callOdooProxy,
     generateRequestId,
   );

   if (result.success) {
     attachSuccess = true;
     break;
   }

   if (attempt < maxAttempts) {
     const delay = getRetryDelay(attempt);
     await new Promise((resolve) => setTimeout(resolve, delay));
   }
 }

 // Silent failure - user will never see this error
 if (!attachSuccess) {
   console.warn('Attachment upload failed after all retries');
 }
};

export const SupportWidget: React.FC<SupportWidgetProps> = () => {
 const [isOpen, setIsOpen] = useState(false);
 const [isMobile, setIsMobile] = useState(false);

 // Cache UID to avoid re-authentication for every ticket submission
 const [cachedUid, setCachedUid] = useState<number | null>(null);
 const [uidTimestamp, setUidTimestamp] = useState<number>(0);
 const UID_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

 // Helper function to call Odoo via Django backend proxy (hides all credentials from Network tab)
 // Uses existing apiService which handles base URL, token injection, and error handling automatically
 const callOdooProxy = useCallback(async (payload: any) => {
   try {
     // Use existing apiService which automatically handles:
     // - Base URL via userCache.getBaseUrl()
     // - Token injection via interceptors
     // - Error handling and token refresh
     const response = await apiService.post('/api/v1/odoo-proxy/', payload);
     return response.data;
   } catch (error: any) {
     console.error('Odoo proxy call failed:', error);
     // Extract error message from axios error response
     const errorMessage = error.response?.data?.error?.message || error.message || 'Request failed';
     throw new Error(errorMessage);
   }
 }, []);

 // Detect screen size
 useEffect(() => {
   const checkScreenSize = () => {
     setIsMobile(window.innerWidth < 768);
   };

   checkScreenSize();
   window.addEventListener('resize', checkScreenSize);
   return () => window.removeEventListener('resize', checkScreenSize);
 }, []);

 // Listen for custom event to open support widget from sign-in page or Help Center menu
 useEffect(() => {
   const handleOpenSupport = () => {
     setIsOpen(true);
     // Track Help Center widget click
     const platform = isMobile ? 'mobile' : 'web';
     const token = userCache.getTokenFromCache?.();
     const loggedIn = Boolean(token);
     const eventData = {
       platform,
       isLoggedIn: loggedIn,
     };
     console.log('[PostHog Event] support_help_center_clicked', eventData);
     posthog?.capture('support_help_center_clicked', eventData);
   };

   window.addEventListener('openSupportWidget', handleOpenSupport);
   return () => {
     window.removeEventListener('openSupportWidget', handleOpenSupport);
   };
 }, [isMobile, posthog]);

 // localStorage keys for form persistence
 const STORAGE_KEYS = {
   phoneNumber: 'support_widget_phoneNumber',
   issueDescription: 'support_widget_issueDescription',
   issueTag: 'support_widget_issueTag',
   attachment: 'support_widget_attachment',
 };

 // Load persisted form data from localStorage
 const loadPersistedFormData = () => {
   try {
     const savedPhoneNumber = localStorage.getItem(STORAGE_KEYS.phoneNumber) || '';
     const savedIssueDescription = localStorage.getItem(STORAGE_KEYS.issueDescription) || '';
     const savedIssueTag = localStorage.getItem(STORAGE_KEYS.issueTag) || '';
    const savedAttachment = localStorage.getItem(STORAGE_KEYS.attachment);
    let parsedAttachment: AttachmentPayload | null = null;
    if (savedAttachment) {
      try {
        parsedAttachment = JSON.parse(savedAttachment);
      } catch (e) {
        // Invalid attachment data in localStorage - clear corrupted data
        console.warn('Failed to parse saved attachment data, clearing corrupted entry:', e);
        try {
          localStorage.removeItem(STORAGE_KEYS.attachment);
        } catch {
          // Ignore if removal also fails
        }
      }
    }
    return {
      phoneNumber: savedPhoneNumber,
      issueDescription: savedIssueDescription,
      issueTag: savedIssueTag,
      attachment: parsedAttachment,
    };
  } catch (e) {
    // localStorage access failed (e.g., private browsing mode, quota exceeded)
    // Return empty form data as fallback
    console.warn('Failed to load persisted form data from localStorage:', e);
    return {
      phoneNumber: '',
      issueDescription: '',
      issueTag: '',
      attachment: null,
    };
  }
 };

// Clear persisted form data from localStorage
const clearPersistedFormData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.phoneNumber);
    localStorage.removeItem(STORAGE_KEYS.issueDescription);
    localStorage.removeItem(STORAGE_KEYS.issueTag);
    localStorage.removeItem(STORAGE_KEYS.attachment);
  } catch (e) {
    // localStorage access failed (e.g., private browsing mode) - non-critical, log and continue
    console.warn('Failed to clear persisted form data from localStorage:', e);
  }
};

 const persistedData = loadPersistedFormData();
 const [phoneNumber, setPhoneNumber] = useState(persistedData.phoneNumber);
 const [issueDescription, setIssueDescription] = useState(persistedData.issueDescription);
 const [issueTag, setIssueTag] = useState(persistedData.issueTag);
 const [attachment, setAttachment] = useState<AttachmentPayload | null>(persistedData.attachment);
 const fileInputRef = useRef<HTMLInputElement>(null);

 const [isSubmitting, setIsSubmitting] = useState(false);
 const [submitError, setSubmitError] = useState<string | null>(null);
 const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

// Auto-dismiss success message after 5 seconds
useEffect(() => {
  if (!submitSuccess) return;
  const timeout = globalThis.setTimeout(() => {
    setSubmitSuccess(null);
  }, 5000);
  return () => {
    globalThis.clearTimeout(timeout);
  };
}, [submitSuccess]);

// Auto-dismiss error message after 5 seconds
useEffect(() => {
  if (!submitError) return;
  const timeout = globalThis.setTimeout(() => {
    setSubmitError(null);
  }, 5000);
  return () => {
    globalThis.clearTimeout(timeout);
  };
}, [submitError]);

 // Tabs (only for signed-in users)
 type TabKey = 'report' | 'history';
 const [activeTab, setActiveTab] = useState<TabKey>('report');
 const [isLoadingTickets, setIsLoadingTickets] = useState(false);
 const [ticketsError, setTicketsError] = useState<string | null>(null);
 const [tickets, setTickets] = useState<any[]>([]);

 // Logged-in context - Check token from cache to determine authentication state
 const [isLoggedIn, setIsLoggedIn] = useState(() => {
   const token = userCache.getTokenFromCache?.();
   return Boolean(token);
 });

 const trackSupportWidgetEvent = useCallback(
   (eventName: string, properties: Record<string, unknown> = {}) => {
     const platform = isMobile ? 'mobile' : 'web';
     const eventData = {
       platform,
       isLoggedIn,
       ...properties,
     };
     // Log to console
     console.log(`[PostHog Event] ${eventName}`, eventData);
     // Send to PostHog
     posthog?.capture(eventName, eventData);
   },
   [isLoggedIn, isMobile],
 );

 // Watch for token changes in cache (token-based authentication check)
 useEffect(() => {
   const checkToken = () => {
     const token = userCache.getTokenFromCache?.();
     setIsLoggedIn(Boolean(token));
   };

   // Check immediately
   checkToken();

   // Set up interval to check token changes (since cache is not reactive)
   const interval = setInterval(checkToken, 500);

   return () => clearInterval(interval);
 }, []);

 const user = useLiveQuery(() => userDB.getUserProfileFromDB());
 const userDetails = useLiveQuery(
   () => (isLoggedIn ? userDB.getCurrentUserProfiles() : undefined),
   [isLoggedIn, user?.username],
 ) as any;
 const activeRolePermissionData: any = useActiveRolePermission?.() as any;

 const profileId = userDetails?.id || user?.id || '';
 const userName = user?.name || user?.username || '';
 const userPhone = userDetails?.phone_number || user?.username || '';
 const userType = userDetails?.type || '';
 const role = activeRolePermissionData?.activeRole?.name || userDetails?.role || '';
 const joiningDate = userDetails?.joiningDate || '';
 const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
 const projectName = useMemo(() => {
   // Directly read from localStorage selectedRegion
   try {
     const stored = localStorage.getItem('selectedRegion');
     if (stored) {
       const parsed = JSON.parse(stored);
       return parsed?.label || parsed?.name || '';
     }
   } catch {}
   // Fallback to cached selected region label
   const region = userCache.getRegionFromCache?.();
   if (region?.label) return region.label;
   return '';
 }, []);
 const { name: deviceName, version: deviceVersion } = useMemo(() => getDeviceNameAndVersion(), []);
 const deviceDisplay = useMemo(() => {
   if (!deviceName && !deviceVersion) return '';
   if (deviceVersion && deviceVersion !== 'Unknown') {
     return `${deviceName} ${deviceVersion}`.trim();
   }
   return deviceName;
 }, [deviceName, deviceVersion]);

 const handleToggle = () => {
   setIsOpen((prev) => {
     const newState = !prev;
     if (prev) {
       window.dispatchEvent(new CustomEvent('closeSupportWidget'));
     }
     return newState;
   });
 };

 useEffect(() => {
   if (!isOpen) {
     return;
   }
   // Load persisted form data when widget opens
   const persistedData = loadPersistedFormData();
   setPhoneNumber(persistedData.phoneNumber);
   setIssueDescription(persistedData.issueDescription);
   setIssueTag(persistedData.issueTag);
   setAttachment(persistedData.attachment);

   trackSupportWidgetEvent('support_widget_opened');
 }, [isOpen, trackSupportWidgetEvent]);

 // Save form fields to localStorage when they change
 useEffect(() => {
   try {
     if (phoneNumber) {
       localStorage.setItem(STORAGE_KEYS.phoneNumber, phoneNumber);
     } else {
       localStorage.removeItem(STORAGE_KEYS.phoneNumber);
     }
   } catch (e) {
     // localStorage access failed (e.g., quota exceeded, private browsing) - non-critical
     console.warn('Failed to persist phone number to localStorage:', e);
   }
 }, [phoneNumber]);

 useEffect(() => {
   try {
     if (issueDescription) {
       localStorage.setItem(STORAGE_KEYS.issueDescription, issueDescription);
     } else {
       localStorage.removeItem(STORAGE_KEYS.issueDescription);
     }
   } catch (e) {
     // localStorage access failed (e.g., quota exceeded, private browsing) - non-critical
     console.warn('Failed to persist issue description to localStorage:', e);
   }
 }, [issueDescription]);

 useEffect(() => {
   try {
     if (attachment) {
       localStorage.setItem(STORAGE_KEYS.attachment, JSON.stringify(attachment));
     } else {
       localStorage.removeItem(STORAGE_KEYS.attachment);
     }
   } catch (e) {
     // localStorage access failed (e.g., quota exceeded, attachment too large) - non-critical
     console.warn('Failed to persist attachment to localStorage:', e);
   }
 }, [attachment]);

 const maxSizeMB = Number(process.env.NX_PUBLIC_ATTACHMENT_MAX_MB || 10);
 const maxBytes = maxSizeMB * 1024 * 1024;

 const fileToBase64 = (file: File) =>
   new Promise<string>((resolve, reject) => {
     const reader = new FileReader();
     reader.onload = () => resolve(reader.result as string);
     reader.onerror = (err) => reject(err);
     reader.readAsDataURL(file);
   });

 const handleFileChange = useCallback(
   async (e: React.ChangeEvent<HTMLInputElement>) => {
     const file = e.target.files?.[0];
     if (!file) {
       setAttachment(null);
       return;
     }
     if (file.size > maxBytes) {
       setSubmitError(`Attachment exceeds ${maxSizeMB} MB limit`);
       setAttachment(null);
       return;
     }

     try {
       const base64 = await fileToBase64(file);
       const contentBase64 = base64.split(',')[1] || base64;

       // Validate base64 data
       if (!contentBase64 || contentBase64.length === 0) {
         setSubmitError('Failed to process file. Please try again.');
         setAttachment(null);
         return;
       }

       // Ensure mimetype is always set (fallback for files without MIME type)
       // For videos and other files, ensure proper MIME type detection
       let mimetype = file.type;
       if (!mimetype || mimetype === 'application/octet-stream') {
         // Try to detect MIME type from file extension
         const extension = file.name.split('.').pop()?.toLowerCase();
         const mimeMap: Record<string, string> = {
           'mp4': 'video/mp4',
           'avi': 'video/x-msvideo',
           'mov': 'video/quicktime',
           'mkv': 'video/x-matroska',
           'webm': 'video/webm',
           'jpg': 'image/jpeg',
           'jpeg': 'image/jpeg',
           'png': 'image/png',
           'gif': 'image/gif',
           'webp': 'image/webp',
           'pdf': 'application/pdf',
           'doc': 'application/msword',
           'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
         };
         mimetype = extension ? (mimeMap[extension] || 'application/octet-stream') : 'application/octet-stream';
       }

       const attachmentData = {
         filename: file.name,
         content_base64: contentBase64,
         mimetype: mimetype,
         size: file.size,
       };
       setAttachment(attachmentData);
       setSubmitError(null); // Clear any previous errors

      // Save attachment to localStorage
      try {
        localStorage.setItem(STORAGE_KEYS.attachment, JSON.stringify(attachmentData));
      } catch (e) {
        // localStorage access failed (e.g., quota exceeded, attachment too large) - non-critical
        console.warn('Failed to save attachment to localStorage:', e);
      }

       // Track file selection
       trackSupportWidgetEvent('support_file_selected', {
         filename: file.name,
         fileSize: file.size,
         mimetype: mimetype,
       });
     } catch (err) {
       console.error('Error processing file:', err);
       setSubmitError('Failed to process file. Please try again.');
       setAttachment(null);
     }
   },
   [maxBytes, maxSizeMB, trackSupportWidgetEvent],
 );

 // Get cached UID or authenticate if cache expired
 const getUid = useCallback(async (): Promise<number> => {
   const now = Date.now();

   // Return cached UID if still valid
   if (cachedUid && (now - uidTimestamp) < UID_CACHE_DURATION) {
     return cachedUid;
   }

   // Authenticate and cache UID
   const authData = await callOdooProxy({
     jsonrpc: '2.0',
     method: 'call',
     params: {
       service: 'common',
       method: 'authenticate',
       args: ['', '', '', {}], // Placeholders - Django backend replaces with actual credentials
     },
     id: generateRequestId(),
   });

   if (authData.error || !authData.result) {
     throw new Error('Odoo authentication failed');
   }

   const uid = authData.result;
   setCachedUid(uid);
   setUidTimestamp(now);

  return uid;
}, [cachedUid, uidTimestamp, UID_CACHE_DURATION, callOdooProxy]);

// Helper functions to process field values by type
const processDateValue = (value: any): any => {
  const trimmedValue = typeof value === 'string' ? value.trim() : value;
  return trimmedValue && trimmedValue !== '' ? trimmedValue : null;
};

const processNumericValue = (value: any): any => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numValue = typeof value === 'string' ? Number(value) : value;
  return !Number.isNaN(numValue) && Number.isFinite(numValue) ? numValue : null;
};

const processStringValue = (value: any): any => {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmedValue = typeof value === 'string' ? value.trim() : value;
  return trimmedValue !== '' && trimmedValue !== null && trimmedValue !== undefined ? trimmedValue : null;
};

// Helper functions to safely set fields - sends null for missing fields instead of omitting
const setFieldIfValue = (obj: any, key: string, value: any) => {
  obj[key] = processStringValue(value);
};

const setDateFieldIfValue = (obj: any, key: string, value: any) => {
  obj[key] = processDateValue(value);
};

const setNumericFieldIfValue = (obj: any, key: string, value: any) => {
  obj[key] = processNumericValue(value);
};

// Types for ticket description data
interface TicketDescriptionData {
  isLoggedIn: boolean;
  userName?: string;
  userPhone?: string;
  profileId?: number;
  role?: string;
  userType?: string;
  deviceDisplay: string;
  joiningDate?: string;
  projectName?: string;
  currentUrl: string;
  phoneNumber: string;
  issueDescription: string;
}

// Build ticket description block
const buildTicketDescription = (data: TicketDescriptionData): string => {
  if (data.isLoggedIn) {
    return [
      'User Details:',
      `Name: ${data.userName || '—'}`,
      `Phone: ${data.userPhone || '—'}`,
      `Profile ID: ${data.profileId || '—'}`,
      `Role: ${data.role || '—'}`,
      `Type: ${data.userType || '—'}`,
      `Device: ${data.deviceDisplay || '—'}`,
      `Joining Date: ${data.joiningDate || '—'}`,
      `Project: ${data.projectName || '—'}`,
      `Current URL: ${data.currentUrl || '—'}`,
      '',
      'Issue:',
      data.issueDescription.trim(),
    ].join('\n');
  }
  return [
    'User Details:',
    `Phone: ${data.phoneNumber.trim()}`,
    '',
    'Issue:',
    data.issueDescription.trim(),
  ].join('\n');
};

// Types for ticket payload data
interface TicketPayloadData {
  ticketSubject: string;
  detailsBlock: string;
  teamId: number;
  isBeforeAuthentication: boolean;
  isLoggedIn: boolean;
  userName?: string;
  userPhone?: string;
  phoneNumber: string;
  deviceName: string;
  deviceVersion: string;
  profileId?: number;
  role?: string;
  userType?: string;
  joiningDate?: string;
  projectName?: string;
  issueTagLabel: string;
  issueDescription: string;
  currentUrl: string;
}

// Set common fields for ticket payload
const setCommonTicketFields = (payload: any, data: TicketPayloadData) => {
  setNumericFieldIfValue(payload, 'team_id', data.teamId);
  setFieldIfValue(payload, 'priority', '0');
  setFieldIfValue(payload, 'partner_name', data.isLoggedIn ? data.userName : data.phoneNumber.trim());
  setFieldIfValue(payload, 'partner_email', data.isLoggedIn ? `${data.userPhone}@placeholder.com` : `${data.phoneNumber.trim()}@placeholder.com`);
  setFieldIfValue(payload, 'device_name', data.deviceName);
  setFieldIfValue(payload, 'device_version', data.deviceVersion);
};

// Set fields for pre-authentication users
const setPreAuthFields = (payload: any, data: TicketPayloadData) => {
  setFieldIfValue(payload, 'phone_number', data.phoneNumber.trim());
  setFieldIfValue(payload, 'issue_tag_before_signin', data.issueTagLabel);
  setFieldIfValue(payload, 'issue_tag_after_signin', data.issueTagLabel);
  setFieldIfValue(payload, 'issue_description', data.issueDescription.trim());
  setFieldIfValue(payload, 'issue_detail', data.issueDescription.trim());
  setFieldIfValue(payload, 'current_active_url', data.currentUrl);
};

// Set fields for logged-in users
const setLoggedInFields = (payload: any, data: TicketPayloadData) => {
  setFieldIfValue(payload, 'user_name', data.userName);
  setFieldIfValue(payload, 'profile_id', data.profileId ? String(data.profileId) : null);
  setFieldIfValue(payload, 'role', data.role);
  setFieldIfValue(payload, 'type', data.userType);
  setDateFieldIfValue(payload, 'joining_date', data.joiningDate);
  setFieldIfValue(payload, 'project_name', data.projectName);
  setFieldIfValue(payload, 'phone_number', (data.userPhone || '').trim());
  setFieldIfValue(payload, 'issue_tag_before_signin', data.issueTagLabel);
  setFieldIfValue(payload, 'issue_tag_after_signin', data.issueTagLabel);
  setFieldIfValue(payload, 'issue_description', data.issueDescription.trim());
  setFieldIfValue(payload, 'issue_detail', data.issueDescription.trim());
  setFieldIfValue(payload, 'current_active_url', data.currentUrl);
};

// Build ticket payload with all required fields
const buildTicketPayload = (data: TicketPayloadData): any => {
  const ticketPayload: any = {
    name: data.ticketSubject,
    description: data.detailsBlock,
  };

  setCommonTicketFields(ticketPayload, data);

  if (data.isBeforeAuthentication) {
    setPreAuthFields(ticketPayload, data);
  } else {
    setLoggedInFields(ticketPayload, data);
  }

  return ticketPayload;
};

const handleSubmit = async () => {
   setSubmitError(null);
   setSubmitSuccess(null);

   // Track submit button click
   trackSupportWidgetEvent('support_submit_button_clicked', {
     hasIssueTag: !!issueTag,
     hasDescription: !!issueDescription.trim(),
     hasAttachment: !!attachment,
     descriptionLength: issueDescription.trim().length,
   });

   if (!isLoggedIn) {
     if (!phoneNumber.trim() || !issueDescription.trim() || !issueTag) {
       setSubmitError('Please fill all required fields');
       return;
     }
   }

   if (isLoggedIn && (!issueTag || !issueDescription.trim())) {
     setSubmitError('Please fill all required fields');
     return;
   }

   // Show success message immediately (generic, without ticket number) - instant response
   const successMessage = isBeforeAuthentication
     ? `Thank you! Your ticket has been created successfully.`
     : `Thank you! Your ticket has been created successfully. You can track its progress in the 'My Tickets' section.`;
   setSubmitSuccess(successMessage);

   // Clear form immediately
   const capturedIssueDescription = issueDescription;
   const capturedIssueTag = issueTag;
   const capturedAttachment = attachment ? {
     filename: attachment.filename,
     content_base64: attachment.content_base64,
     mimetype: attachment.mimetype || 'application/octet-stream',
     size: attachment.size,
   } : null;

   setPhoneNumber('');
   setIssueDescription('');
   setIssueTag('');
   setAttachment(null);
   setIsSubmitting(false);

   // Clear persisted form data from localStorage
   clearPersistedFormData();

   // Run all API calls in background (create ticket, read ticket, upload attachment)
   // All calls go through Django backend proxy to hide credentials
   (async () => {
     try {
       const uid = await getUid();
       const teamId = getTeamIdByIssueTag(capturedIssueTag);
       const issueTagLabel = (ALL_ISSUE_OPTIONS.find((opt) => opt.value === capturedIssueTag)?.content || capturedIssueTag || '').toString();

       const detailsBlock = buildTicketDescription({
         isLoggedIn,
         userName,
         userPhone,
         profileId,
         role,
         userType,
         deviceDisplay,
         joiningDate,
         projectName,
         currentUrl,
         phoneNumber,
         issueDescription: capturedIssueDescription,
       });

       const ticketSubject = isLoggedIn
         ? `Support Request - ${issueTagLabel || capturedIssueTag} - ${userName || userPhone}`
         : `Support Request - ${issueTagLabel || capturedIssueTag} - ${phoneNumber.trim()}`;

       const ticketPayload = buildTicketPayload({
         ticketSubject,
         detailsBlock,
         teamId,
         isBeforeAuthentication,
         isLoggedIn,
         userName,
         userPhone,
         phoneNumber,
         deviceName,
         deviceVersion,
         profileId,
         role,
         userType,
         joiningDate,
         projectName,
         issueTagLabel,
         issueDescription: capturedIssueDescription,
         currentUrl,
       });

       const createData = await callOdooProxy({
         jsonrpc: '2.0',
         method: 'call',
         params: {
           service: 'object',
           method: 'execute_kw',
           args: ['', uid, '', 'helpdesk.ticket', 'create', [ticketPayload]],
         },
         id: generateRequestId(),
       });

       if (createData.error) {
         setSubmitError(createData.error?.data?.message || 'Failed to create ticket');
         return;
       }

       const ticketId = createData.result;
       trackSupportWidgetEvent('support_ticket_submitted_successfully', {
         ticketId,
         issueTag: capturedIssueTag,
         hasAttachment: !!capturedAttachment,
         authState: isBeforeAuthentication ? 'pre_login' : 'post_login',
       });

       if (isLoggedIn) {
         const newTicket = {
           id: ticketId,
           ticket_number: `#${ticketId}`,
           subject: `#${ticketId}`,
           title: `#${ticketId}`,
           description: detailsBlock,
           status: 'New',
           tag: capturedIssueTag,
           created_at: new Date().toISOString(),
         };
         setTickets((prev) => [newTicket, ...prev]);
       }

       if (capturedAttachment) {
         uploadAttachmentWithRetry(uid, ticketId, capturedAttachment, callOdooProxy, generateRequestId).catch((err) => {
           console.warn('Attachment upload error:', err);
         });
       }

       try {
         const readData = await callOdooProxy({
           jsonrpc: '2.0',
           method: 'call',
           params: {
             service: 'object',
             method: 'execute_kw',
             args: ['', uid, '', 'helpdesk.ticket', 'read', [[ticketId], getTicketDetailFields()]],
           },
           id: generateRequestId(),
         });

         const ticketDetails = readData.result?.[0] || {};
         const exactTicketNumber = ticketDetails.number || `#${ticketId}`;
         const stageId = ticketDetails.stageId || ticketDetails.stage_id;
         const stageName = (stageId && Array.isArray(stageId) && stageId.length >= 2 ? stageId[1] : null) || 'New';

         if (!isLoggedIn) {
           return;
         }

         const updateTicketInList = (t: any) => {
           if (t.id === ticketId) {
             return { ...t, ticket_number: exactTicketNumber, subject: exactTicketNumber, title: exactTicketNumber, status: stageName, created_at: t.created_at };
           }
           return t;
         };

         setTickets((prev) => prev.map(updateTicketInList));
       } catch (err) {
         console.warn('Failed to fetch exact ticket number:', err);
       }
     } catch (e: any) {
       setSubmitError(e?.message || 'Network error');
     }
   })();
 };

 const fetchTickets = async () => {
   if (!isLoggedIn) return;
   setIsLoadingTickets(true);
   setTicketsError(null);
   try {
     // Step 1: Get UID (uses cached UID or authenticates if needed) - credentials handled by Django backend
     const uid = await getUid();

     // Step 2: Search tickets by user_name field (to fetch all tickets for this user) - credentials handled by Django backend
     const searchUserName = userName || '';
     if (!searchUserName) {
       setTickets([]);
       setIsLoadingTickets(false);
       return;
     }

     const searchData = await callOdooProxy({
       jsonrpc: '2.0',
       method: 'call',
       params: {
         service: 'object',
         method: 'execute_kw',
         args: [
           '', // db placeholder - Django backend replaces
           uid,
           '', // password placeholder - Django backend replaces
           'helpdesk.ticket',
           'search',
           [[['user_name', '=', searchUserName]]],
           { order: 'create_date desc' },
         ],
       },
       id: generateRequestId(),
     });
     if (searchData.error) {
       throw new Error('Failed to search tickets');
     }
     const ticketIds = searchData.result || [];

     if (ticketIds.length === 0) {
       setTickets([]);
       setIsLoadingTickets(false);
       return;
     }

     // Step 3: Read ticket details including the number field - credentials handled by Django backend
     const readData = await callOdooProxy({
       jsonrpc: '2.0',
       method: 'call',
       params: {
         service: 'object',
         method: 'execute_kw',
         args: [
           '', // db placeholder - Django backend replaces
           uid,
           '', // password placeholder - Django backend replaces
           'helpdesk.ticket',
           'read',
           [
             ticketIds,
             getTicketListFields(),
           ],
         ],
       },
       id: generateRequestId(),
     });
     if (readData.error) {
       throw new Error('Failed to read tickets');
     }

     // Step 4: Format tickets with truncated descriptions
     const formatDescription = (desc: string) => {
       if (!desc) return '';
       const words = desc.split(/\s+/);
       return words.slice(0, 100).join(' ') + (words.length > 100 ? '...' : '');
     };

     // No need to filter - Odoo search already filtered by user_name
     // All tickets in readData.result are already for the current user
     // Since search uses user_name field, all returned tickets belong to this user
     const formattedTickets = (readData.result || []).map((ticket: any) => {
       // Extract ticket number - exactly as it works currently
       const ticketNumber = ticket.number || `#${ticket.id}`;

       // Extract status from stageId[1] - apiService converts snake_case to camelCase
       // stageId is an array: [id, name] - we need the name (index 1)
       // Example: [1, "Submitted"] -> extract "Submitted"
       // Note: apiService converts stage_id to stageId, so use stageId
       let stageName = 'New';
       const stageId = ticket.stageId || ticket.stage_id; // Handle both camelCase and snake_case
       if (stageId !== undefined && stageId !== null) {
         if (Array.isArray(stageId) && stageId.length >= 2) {
           const stageNameValue = stageId[1];
           if (stageNameValue) {
             stageName = stageNameValue;
           }
         }
       }

       // Extract createDate - apiService converts snake_case to camelCase
       // createDate is a string: "2025-12-10 18:26:46"
       // Note: apiService converts create_date to createDate, so use createDate
       const created_at = ticket.createDate || ticket.create_date || '';

       return {
         id: ticket.id,
         ticket_number: ticketNumber,
         subject: ticketNumber,
         title: ticketNumber,
         description: formatDescription(ticket.description || ''),
         status: stageName, // Status from stageId[1] or stage_id[1]
         tag: '',
         created_at: created_at, // createDate or create_date from JSON
       };
     });

     // Already sorted by create_date desc from Odoo query
     setTickets(formattedTickets);
   } catch (e: any) {
     setTicketsError(e?.message || 'Network error');
     setTickets([]);
   } finally {
     setIsLoadingTickets(false);
   }
 };

 // Check authentication state based on token from cache
 // Before login (no token): Show form with phone number
 // After login (token exists): Show form with tabs (Report Issue/My Tickets), no phone number
 const isBeforeAuthentication = !isLoggedIn;
 // Show full screen form only if user is before authentication (no token)
 // After login, always show tabs regardless of how widget was opened
 const usingFullScreenForm = isBeforeAuthentication;

 // Add "Choose Issue Type" as placeholder option
 const placeholderOption: SelectOption = { value: '', content: 'Choose Issue Type' };
 const currentOptions = [placeholderOption, ...(usingFullScreenForm ? LOGIN_ISSUE_OPTIONS : LOGGED_IN_ISSUE_OPTIONS)];

 const handleSelectChange = useCallback(
   (option: SelectOption) => {
     // Don't track if placeholder is selected
     if (option.value === '') {
      setIssueTag('');
      // Clear from localStorage
      try {
        localStorage.removeItem(STORAGE_KEYS.issueTag);
      } catch (e) {
        // localStorage access failed (e.g., private browsing mode) - non-critical
        console.warn('Failed to clear issue tag from localStorage:', e);
      }
       return;
     }
    setIssueTag(option.value);
    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEYS.issueTag, option.value);
    } catch (e) {
      // localStorage access failed (e.g., quota exceeded, private browsing) - non-critical
      console.warn('Failed to persist issue tag to localStorage:', e);
    }
     const selectedOption =
       ALL_ISSUE_OPTIONS.find((opt) => opt.value === option.value) || option;
     const issueLabel =
       typeof selectedOption.content === 'string'
         ? selectedOption.content
         : option.value;
     trackSupportWidgetEvent('support_widget_issue_type_selected', {
       issueTag: option.value,
       issueLabel,
       authState: usingFullScreenForm ? 'pre_login' : 'post_login',
     });
   },
   [trackSupportWidgetEvent, usingFullScreenForm],
 );

 const renderIssueTypeSelect = useCallback(
   () => (
     <FormField>
       <DSMSelect
         key={`issue-type-select-${issueTag || 'empty'}`}
         variant="outlined"
         label="Choose issue type"
         options={currentOptions}
         defaultValue={issueTag || ''}
         onChange={handleSelectChange}
         width="100%"
       />
     </FormField>
   ),
   [currentOptions, handleSelectChange, issueTag],
 );

 const renderAttachmentField = useCallback(
   (inputId: string) => (
     <FormField>
       <Typography variant="textLabelMediumSurface">
         Add an Image or File
       </Typography>
       <FileUploadWrapper>
         <FileUploadInput
           ref={fileInputRef}
           type="file"
           accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
           onChange={handleFileChange}
           id={inputId}
         />
         <Button
           variant="outlined"
           onClick={() => {
             // Track "Choose file" button click
             trackSupportWidgetEvent('support_choose_file_button_clicked', {
               hasExistingAttachment: !!attachment,
             });
             fileInputRef.current?.click();
           }}
           width="100%"
           icon="attach_file"
         >
           {attachment ? attachment.filename : 'Choose file'}
         </Button>
       </FileUploadWrapper>
     </FormField>
   ),
   [attachment, handleFileChange, trackSupportWidgetEvent],
 );

 return (
   <Container aria-label="support-widget">
     {isOpen && (
           <>
             {!isMobile && <Backdrop onClick={handleToggle} />}
             <FullScreenDialog role="dialog" aria-modal="true" data-mobile={isMobile}>
             <FullScreenHeader>
               <CloseButton onClick={handleToggle} aria-label="Close support dialog">✕</CloseButton>
             <TitleWrapper>
               <Typography variant="textHeadlineMediumOnSurface" textAlign="center">
                 Help Support
               </Typography>
             </TitleWrapper>
             </FullScreenHeader>
             <FullScreenBody>
             {usingFullScreenForm ? (
               <>
               <Form>
                   <HelpTextWrapper>
                     <Typography variant="textBodyMediumPrimary">
                       Our Support Team will contact you soon after you submit this request.
                     </Typography>
                   </HelpTextWrapper>
                   {/* Only show phone number field if user is before authentication (no token) */}
                   {isBeforeAuthentication && (
                     <PhoneFieldWrapper>
                       <PhoneLabel>Phone Number</PhoneLabel>
                       <TextField
                     name="phone"
                     type="tel"
                         variant="outlined"
                         placeholder="03001234567"
                     value={phoneNumber}
                         autocomplete="tel"
                       onKeyPress={(e) => {
                         if (!/[0-9]/.test(e.key)) {
                           e.preventDefault();
                         }
                       }}
                         onInput={(e) => {
                       const value = e.target.value.replace(/\D/g, '');
                       setPhoneNumber(value);
                     }}
                         width="100%"
                       />
                     </PhoneFieldWrapper>
                   )}
                   <DescriptionFieldWrapper>
                     <DescriptionLabel>Description</DescriptionLabel>
                     <TextField
                     name="description"
                       type="textarea"
                       variant="outlined"
                       placeholder="Describe your issue in detail"
                     value={issueDescription}
                     rows={5}
                       onInput={(e) => {
                         setIssueDescription(e.target.value);
                         // Track description box interaction (debounced - only on first interaction)
                         if (e.target.value.length === 1) {
                           trackSupportWidgetEvent('support_description_box_focused');
                         }
                       }}
                     width="100%"
                   />
                   </DescriptionFieldWrapper>
                 {renderIssueTypeSelect()}
                 {renderAttachmentField('file-upload')}
                 {submitError && <ErrorText>{submitError}</ErrorText>}
                 {submitSuccess && <SuccessText>{submitSuccess}</SuccessText>}
               </Form>
               <FullScreenActions>
                   <Button
                     variant="outlined"
                     onClick={handleToggle}
                     width="100%"
                   >
                     Cancel
                   </Button>
                   <Button
                     variant="filled"
                     onClick={handleSubmit}
                     disabled={isSubmitting}
                     loading={isSubmitting}
                     width="100%"
                   >
                     Submit
                   </Button>
               </FullScreenActions>
           </>
         ) : (
             <Body>
               <Tabs>
                 <TabButton
                   data-active={activeTab === 'report'}
                   onClick={() => {
                     setActiveTab('report');
                     trackSupportWidgetEvent('support_tab_report_issue_clicked');
                   }}
                 >
                   <Typography variant={activeTab === 'report' ? 'textLabelLargeOnPrimary' : 'textLabelLargeOnSurface'}>
                   Report Issue
                   </Typography>
                 </TabButton>
                 <TabButton
                   data-active={activeTab === 'history'}
                   onClick={() => {
                     setActiveTab('history');
                     void fetchTickets();
                     trackSupportWidgetEvent('support_tab_my_tickets_clicked');
                   }}
                 >
                   <Typography variant={activeTab === 'history' ? 'textLabelLargeOnPrimary' : 'textLabelLargeOnSurface'}>
                   My Tickets
                   </Typography>
                 </TabButton>
               </Tabs>

           {(isLoggedIn && activeTab === 'report') && (
             <Form>
                     <DescriptionFieldWrapper>
                       <DescriptionLabel>Description</DescriptionLabel>
                       <TextField
                         name="description"
                         type="textarea"
                 variant="outlined"
                         placeholder="Describe your issue in detail"
                         value={issueDescription}
                         rows={5}
                         onInput={(e) => {
                           setIssueDescription(e.target.value);
                           // Track description box interaction (debounced - only on first interaction)
                           if (e.target.value.length === 1) {
                             trackSupportWidgetEvent('support_description_box_focused');
                           }
                         }}
                 width="100%"
               />
                     </DescriptionFieldWrapper>
                     {renderIssueTypeSelect()}
                     {renderAttachmentField('file-upload-loggedin')}
                     <Button
                       variant="filled"
                       onClick={handleSubmit}
                       disabled={isSubmitting}
                       loading={isSubmitting}
                       width="100%"
                     >
                       Submit
                     </Button>
               {submitError && <ErrorText>{submitError}</ErrorText>}
               {submitSuccess && <SuccessText>{submitSuccess}</SuccessText>}
             </Form>
           )}

           {isLoggedIn && activeTab === 'history' ? (
             <TicketsSection>
               {isLoadingTickets && (
                 <Typography variant="textBodyMediumOnSurfaceVariant">
                   Loading tickets…
                 </Typography>
               )}
               {ticketsError && <ErrorText>{ticketsError}</ErrorText>}
               {!isLoadingTickets && !ticketsError && (
                 <TicketList>
                   {tickets.length === 0 && (
                     <Typography variant="textBodyMediumOnSurfaceVariant">
                       No tickets found.
                     </Typography>
                   )}
                   {tickets.map((t: any, idx) => {
                     // Format date as "dd-mmm-yy HH:MM AM/PM" (e.g., "09-Dec-25 02:57 PM")
                     // Odoo returns UTC time in format "YYYY-MM-DD HH:MM:SS" - treat it as UTC
                     const formatDate = (dateStr: string) => {
                       if (!dateStr) return '—';
                       try {
                         // Odoo returns date in format "2025-12-10 18:26:46" (UTC)
                         // Parse it as UTC by appending 'Z' or using UTC parsing
                         let date: Date;
                         if (dateStr.includes('T') || dateStr.includes('Z')) {
                           // Already has timezone info
                           date = new Date(dateStr);
                         } else {
                           // Odoo format: "YYYY-MM-DD HH:MM:SS" - treat as UTC
                           // Append 'Z' to indicate UTC, or use UTC parsing
                           date = new Date(dateStr.replace(' ', 'T') + 'Z');
                         }

                         // Convert to Pakistani timezone using Intl.DateTimeFormat
                         const formatter = new Intl.DateTimeFormat('en-US', {
                           timeZone: 'Asia/Karachi',
                           year: 'numeric',
                           month: 'short',
                           day: '2-digit',
                           hour: '2-digit',
                           minute: '2-digit',
                           hour12: true,
                         });

                         // Format the date parts
                         const parts = formatter.formatToParts(date);
                         const day = parts.find(p => p.type === 'day')?.value.padStart(2, '0') || '00';
                         const month = parts.find(p => p.type === 'month')?.value || 'Jan';
                         const year = parts.find(p => p.type === 'year')?.value.slice(-2) || '25';
                         const hour = parts.find(p => p.type === 'hour')?.value.padStart(2, '0') || '00';
                         const minute = parts.find(p => p.type === 'minute')?.value.padStart(2, '0') || '00';
                         const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value.toUpperCase() || 'AM';

                         // Return format: "dd-mmm-yy HH:MM AM/PM"
                         return `${day}-${month}-${year} ${hour}:${minute} ${dayPeriod}`;
                       } catch {
                         return dateStr;
                       }
                     };

                     const status = t.status || t.state || '—';

                     const displayTicketNumber = t.ticket_number ? t.ticket_number.slice(0, 7) : `#${t.id}`;

                     return (
                       <TicketItem key={t.id || idx}>
                         <TicketNumberWrapper>
                           <Typography variant="textLabelLargePrimary">
                           {displayTicketNumber}
                           </Typography>
                         </TicketNumberWrapper>
                         <StatusRow>
                           <Typography variant="textLabelMediumSurface" inline>
                             Status:{' '}
                           </Typography>
                           <Typography
                             variant="textLabelMediumSurfaceVariantSuccess"
                             inline
                           >
                             {status}
                           </Typography>
                         </StatusRow>
                         <DateRow>
                           <Typography variant="textLabelMediumSurface" inline>
                             Submission Date:{' '}
                           </Typography>
                           <Typography variant="textBodySmallOnSurface" inline>
                             {formatDate(t.created_at || t.create_date)}
                           </Typography>
                         </DateRow>
                         {t.description && (
                           <DetailsSection>
                             <Typography variant="textLabelMediumSurface">
                               Issue Details:
                             </Typography>
                             <Typography variant="textBodySmallOnSurface" ellipseLine={1}>
                               {extractIssueSummary(t.description)}
                             </Typography>
                           </DetailsSection>
                         )}
                       </TicketItem>
                     );
                   })}
                 </TicketList>
               )}
             </TicketsSection>
           ) : null}
             </Body>
         )}
           </FullScreenBody>
         </FullScreenDialog>
       </>
     )}
   </Container>
 );
};

const Container = styled.div`
 position: fixed;
 right: 16px;
 bottom: 16px;
 z-index: 10000;
`;

const CloseButton = styled.button`
 background: transparent;
 border: none;
 cursor: pointer;
 font-size: 16px;
`;

const Body = styled.div`
 padding: 12px;
 display: flex;
 flex-direction: column;
 gap: 12px;
`;

const Select = styled.select`
 border: 1px solid rgba(0,0,0,0.12);
 border-radius: 8px;
 padding: 8px 10px;
 font-size: 14px;
 background: white;
 position: relative;
 z-index: 10;
 appearance: none;
 background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
 background-repeat: no-repeat;
 background-position: right 10px center;
 padding-right: 32px;
 cursor: pointer;

 &:focus {
   outline: 2px solid var(--md-sys-color-primary);
   outline-offset: 2px;
   border-color: var(--md-sys-color-primary);
 }

 @media (min-width: 768px) {
   padding: 10px 12px;
   padding-right: 36px;
   font-size: 15px;
 }
`;

/* Removed user info styles for simplified before sign-in widget */

const Form = styled.div`
 display: flex;
 flex-direction: column;
 gap: 8px;
`;

const Tabs = styled.div`
 display: flex;
 gap: 8px;
`;

const TabButton = styled.button`
 background: transparent;
 border: 1px solid rgba(0,0,0,0.12);
 padding: 6px 10px;
 border-radius: 8px;
 cursor: pointer;
 display: flex;
 align-items: center;
 justify-content: center;

 &[data-active='true'] {
   background: var(--md-sys-color-primary);
   border-color: var(--md-sys-color-primary);
 }
`;

const TicketsSection = styled.div`
 display: flex;
 flex-direction: column;
 gap: 8px;
`;

const TicketList = styled.div`
 display: flex;
 flex-direction: column;
 gap: 8px;
 overflow: auto;

 @media (min-width: 768px) {
   max-height: 220px;
 }
`;

const TicketItem = styled.div`
 border: 1px solid rgba(0,0,0,0.12);
 border-radius: 8px;
 padding: 8px;
`;

const TicketNumberWrapper = styled.div`
 margin-bottom: 8px;
`;

const StatusRow = styled.div`
 display: flex;
 align-items: center;
 gap: 6px;
 margin-bottom: 4px;
`;

const DateRow = styled.div`
 margin-bottom: 4px;
`;

const DetailsSection = styled.div`
 display: flex;
 flex-direction: column;
 gap: 4px;
 margin-top: 6px;
`;

const ErrorText = styled.div`
 color: #b3261e;
 font-size: 12px;
`;

const SuccessText = styled.div`
 color: #0b6b0b;
 font-size: 12px;
`;

// Backdrop overlay for desktop modal
const Backdrop = styled.div`
 position: fixed;
 top: 0;
 left: 0;
 right: 0;
 bottom: 0;
 background: rgba(0, 0, 0, 0.5);
 z-index: 10000;
 display: none;

 @media (min-width: 768px) {
   display: block;
 }
`;

// Full-screen dialog styles for login page
const FullScreenDialog = styled.div`
 position: fixed;
 top: 0;
 left: 0;
 right: 0;
 bottom: 0;
 background: white;
 z-index: 10001;
 display: flex;
 flex-direction: column;
 overflow-y: auto;

 @media (min-width: 768px) {
   &[data-mobile="false"] {
     top: 50%;
     left: 50%;
     right: auto;
     bottom: auto;
     transform: translate(-50%, -50%);
     width: 90%;
     max-width: 600px;
     max-height: 90vh;
     border-radius: 12px;
     box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
     overflow: hidden;
   }
 }

 @media (max-width: 767px) {
   &[data-mobile="true"] {
     top: 0;
     left: 0;
     right: 0;
     bottom: 0;
     transform: none;
     width: 100%;
     max-width: 100%;
     max-height: 100vh;
     border-radius: 0;
   }
 }
`;

const FullScreenHeader = styled.div`
 display: flex;
 align-items: center;
 justify-content: space-between;
 padding: 16px 20px;
 border-bottom: 1px solid rgba(0, 0, 0, 0.12);
 position: sticky;
 top: 0;
 background: white;
 z-index: 1;
 flex-shrink: 0;

 @media (min-width: 768px) {
   padding: 20px 24px;
 }
`;

const TitleWrapper = styled.div`
 flex: 1;
 display: flex;
 align-items: center;
 justify-content: center;
`;

const HelpTextWrapper = styled.div`
 margin-bottom: 16px;

 p,
 h1,
 h2,
 h3,
 h4,
 h5,
 h6,
 span {
   font-weight: 700 !important;
 }
`;

const FullScreenBody = styled.div`
 flex: 1;
 padding: 20px;
 display: flex;
 flex-direction: column;
 gap: 20px;
 overflow-y: auto;

 @media (min-width: 768px) {
   padding: 24px;
   gap: 24px;
 }
`;

const FormField = styled.div`
 display: flex;
 flex-direction: column;
 gap: 8px;

 @media (min-width: 768px) {
   gap: 10px;
 }
`;

const Label = styled.div`
 font-family: Poppins, sans-serif;
 font-weight: 400;
 font-size: 12px;
 line-height: 24px;
 letter-spacing: 0;
 color: rgba(107, 114, 128, 1);
`;

const PhoneFieldWrapper = styled.div`
 display: flex;
 flex-direction: column;
 position: relative;
 margin-top: 8px;
`;

const PhoneLabel = styled.label`
 position: absolute;
 top: -8px;
 left: 12px;
 font-family: Poppins, sans-serif;
 font-weight: 400;
 font-style: normal;
 font-size: 12px;
 line-height: 24px;
 letter-spacing: 0;
 color: rgba(107, 114, 128, 1);
 background: white;
 padding: 0 4px;
 z-index: 10;
 pointer-events: none;
 display: block;
 height: 24px;
 opacity: 1;
 white-space: nowrap;
`;

const DescriptionFieldWrapper = styled.div`
 display: flex;
 flex-direction: column;
 position: relative;
 margin-top: 8px;
`;

const DescriptionLabel = styled.label`
 position: absolute;
 top: -8px;
 left: 12px;
 font-family: Poppins, sans-serif;
 font-weight: 400;
 font-style: normal;
 font-size: 12px;
 line-height: 24px;
 letter-spacing: 0;
 color: rgba(107, 114, 128, 1);
 background: white;
 padding: 0 4px;
 z-index: 10;
 pointer-events: none;
 display: block;
 height: 24px;
 opacity: 1;
 white-space: nowrap;
`;

const FileUploadWrapper = styled.div`
 position: relative;
`;

const FileUploadInput = styled.input`
 position: absolute;
 opacity: 0;
 width: 0;
 height: 0;
`;

const FullScreenActions = styled.div`
 display: flex;
 gap: 12px;
 padding-top: 20px;
 border-top: 1px solid rgba(0, 0, 0, 0.12);
 margin-top: auto;
 flex-shrink: 0;

 > * {
 flex: 1;
 }

 @media (min-width: 768px) {
   padding-top: 24px;
   gap: 16px;
 }
`;
